import axios from "axios";

import {
  baseUrl,
  KOUNT_NEW_ACCOUNT_OPENING,
  STORE_CLIENT_DATA,
} from "../constants";
import { subscribeKlaviyo, trackKlaviyo } from "./klaviyo";
import { collectKountData } from "./kount";
import { normalizeUkMobile } from "./utils";

const DEFAULT_KOUNT_FALLBACK_MOBILE = "07123456789";

const trimToUndefined = (value) => {
  if (typeof value !== "string") {
    return value ?? undefined;
  }

  const trimmed = value.trim();
  return trimmed || undefined;
};

const normalizePhoneNumber = (value) => {
  return normalizeUkMobile(value) || undefined;
};

const getKountMobileDetails = (payload) => {
  const actualMobile = normalizePhoneNumber(payload?.mobile);
  if (actualMobile) {
    return {
      mobileNumber: actualMobile,
      mobileProvided: true,
      mobileSource: "user",
    };
  }

  const fallbackMobile = normalizePhoneNumber(
    process.env.REACT_APP_KOUNT_FALLBACK_MOBILE ||
      DEFAULT_KOUNT_FALLBACK_MOBILE,
  );

  return {
    mobileNumber: fallbackMobile,
    mobileProvided: false,
    mobileSource: "synthetic",
  };
};

const normalizeDateOfBirth = (value) => {
  const trimmed = trimToUndefined(value);
  if (!trimmed) {
    return undefined;
  }

  const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${year}-${month}-${day}`;
  }

  return trimmed;
};

const buildKountAddress = (address) => {
  const currentAddress = {
    line1: trimToUndefined(address?.Line1 ?? address?.address_line_1),
    line2: trimToUndefined(address?.Line2 ?? address?.address_line_2),
    city: trimToUndefined(address?.City ?? address?.address_city),
    postcode: trimToUndefined(address?.PostalCode ?? address?.address_postcode),
  };

  return Object.values(currentAddress).some(Boolean)
    ? currentAddress
    : undefined;
};

export const buildKountIdentityPayload = (payload) => {
  const kountMobile = getKountMobileDetails(payload);
  const kountResponse = payload?.kountResponse || {};

  return {
    sessionId: kountResponse.kountSessionId,
    clientReference:
      trimToUndefined(payload?.uniqueID) ||
      trimToUndefined(payload?.email) ||
      kountResponse.kountSessionId,
    title: trimToUndefined(payload?.title),
    forename: trimToUndefined(payload?.firstName) || "",
    middleName: trimToUndefined(payload?.middleName),
    surname: trimToUndefined(payload?.surname) || "",
    dateOfBirth: normalizeDateOfBirth(payload?.dob),
    email: trimToUndefined(payload?.email),
    mobileNumber: kountMobile.mobileNumber,
    currentAddress: buildKountAddress(payload?.currentAddress),
    kountMobileNumberProvided: kountMobile.mobileProvided,
    kountMobileNumberUsed: kountMobile.mobileNumber,
    kountMobileNumberSource: kountMobile.mobileSource,
  };
};

const fetchWebsiteAcquisitionIpAddress = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/public/ip_address`);
    return response?.data?.address || null;
  } catch {
    return null;
  }
};

export const buildSubmissionPayload = async (payload) => {
  const kountData = await collectKountData(payload?.email);
  const mobile = normalizePhoneNumber(payload?.mobile);

  return {
    ...payload,
    ...(payload?.mobile !== undefined && { mobile: mobile || "" }),
    kountResponse: {
      ...(payload.kountResponse || {}),
      ...kountData,
    },
  };
};

export const submitClientWithKount = async (
  payload,
  { pageName = "Signature", signatureValue, emailRoute } = {},
) => {
  const clientIp =
    payload?.websiteAquisitionIpAddress || (await fetchWebsiteAcquisitionIpAddress());
  const { ipAddress: _ipAddress, ...payloadWithoutIp } = payload || {};
  const payloadWithSignature =
    signatureValue === undefined
      ? payloadWithoutIp
      : { ...payloadWithoutIp, signature: signatureValue };

  const submissionPayload = await buildSubmissionPayload({
    ...payloadWithSignature,
    websiteAquisitionIpAddress: clientIp,
  });
  const identityPayload = buildKountIdentityPayload(submissionPayload);

  let finalPayload;
  let kountError = "";

  try {
    const kountResponse = await KOUNT_NEW_ACCOUNT_OPENING(identityPayload);
    const decision = `${kountResponse?.decision || ""}`.trim().toUpperCase();
    const transactionId = kountResponse?.transactionId || null;
    const status = `${kountResponse?.status || ""}`.trim().toUpperCase() || null;

    finalPayload = {
      ...submissionPayload,
      kountRequest: identityPayload,
      kountResponse: {
        ...(submissionPayload.kountResponse || {}),
        kountMobileNumberProvided:
          identityPayload.kountMobileNumberProvided ?? null,
        kountMobileNumberUsed: identityPayload.kountMobileNumberUsed || null,
        kountMobileNumberSource:
          identityPayload.kountMobileNumberSource || null,
        kountDecision: decision || null,
        kountTransactionId: transactionId,
        kountStatus: status,
        kountDecisionedAt: new Date().toISOString(),
      },
    };
  } catch (error) {
    kountError =
      error?.response?.data?.detail ||
      error?.message ||
      "We couldn't complete the KOUNT verification step right now.";

    finalPayload = {
      ...submissionPayload,
      kountRequest: identityPayload,
      kountResponse: {
        ...(submissionPayload.kountResponse || {}),
        kountMobileNumberProvided:
          identityPayload.kountMobileNumberProvided ?? null,
        kountMobileNumberUsed: identityPayload.kountMobileNumberUsed || null,
        kountMobileNumberSource:
          identityPayload.kountMobileNumberSource || null,
        kountDecision: null,
        kountTransactionId: null,
        kountStatus: "FAILED",
        kountDecisionedAt: new Date().toISOString(),
        kountError,
      },
    };
  }

  const response = await STORE_CLIENT_DATA({
    ...finalPayload,
    ...(emailRoute && { emailRoute }),
  });

  subscribeKlaviyo(finalPayload.email, window.location.href, finalPayload.mobile, {
    firstName: finalPayload.firstName,
    lastName: finalPayload.surname,
  });
  trackKlaviyo("Submitted", {
    url: window.location.href,
    page_name: pageName,
    email: finalPayload.email,
  });

  return {
    response,
    payload: finalPayload,
    kountError,
  };
};
